import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, Flame, Wind, Wrench, Fan } from 'lucide-react';

const servicesMenu = [
  {
    name: 'Heating',
    icon: Flame,
    path: '/services/heating',
    submenu: [
      { name: 'Furnace Repair', path: '/services/heating/furnace-repair' },
      { name: 'Furnace Replacement', path: '/services/heating/furnace-replacement' },
      { name: 'Furnace Maintenance', path: '/services/heating/furnace-maintenance' },
    ]
  },
  {
    name: 'Cooling',
    icon: Wind,
    path: '/services/cooling',
    submenu: [
      { name: 'AC Repair', path: '/services/cooling/ac-repair' },
      { name: 'AC Replacement', path: '/services/cooling/ac-replacement' },
      { name: 'AC Maintenance', path: '/services/cooling/ac-maintenance' },
    ]
  },
  {
    name: 'Installation',
    icon: Wrench,
    path: '/services/installation',
    submenu: []
  },
  {
    name: 'Air Quality',
    icon: Fan,
    path: '/services/air-quality',
    submenu: []
  }
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const location = useLocation();
  const dropdownTimeout = useRef(null);
  const submenuTimeout = useRef(null);

  const isActive = (path) => location.pathname === path;

  const handleDropdownEnter = () => {
    if (dropdownTimeout.current) {
      clearTimeout(dropdownTimeout.current);
    }
    setServicesDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => {
      setServicesDropdownOpen(false);
      setActiveSubmenu(null);
    }, 150);
  };

  const handleSubmenuEnter = (serviceName) => {
    if (submenuTimeout.current) {
      clearTimeout(submenuTimeout.current);
    }
    setActiveSubmenu(serviceName);
  };

  const handleSubmenuLeave = () => {
    submenuTimeout.current = setTimeout(() => {
      setActiveSubmenu(null);
    }, 150);
  };

  return (
    <header className="sticky top-0 w-full bg-white/95 backdrop-blur-sm border-b border-stone-100 z-50">
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href="mailto:info@comfortmasterheating.com" className="hover:text-emerald-400 transition-colors" data-testid="email-link">
              info@comfortmasterheating.com
            </a>
            <span className="hidden sm:inline text-stone-400">|</span>
            <span className="hidden sm:inline">51 Wichita Drive, Nicholasville, KY 40356</span>
          </div>
          <a href="tel:8593576803" className="flex items-center gap-2 font-bold hover:text-emerald-400 transition-colors" data-testid="phone-link-top">
            <Phone size={14} />
            (859) 357-6803
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="/images/assets/old-logo.jpg" 
              alt="ComfortMaster Heating & Air" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className={`nav-link ${isActive('/') ? 'text-emerald-600' : ''}`} data-testid="nav-home">Home</Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleDropdownEnter}
              onMouseLeave={handleDropdownLeave}
            >
              <button 
                className={`nav-link flex items-center gap-1 py-6 ${location.pathname.startsWith('/services') ? 'text-emerald-600' : ''}`} 
                data-testid="nav-services"
              >
                Services <ChevronDown size={16} className={`transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {servicesDropdownOpen && (
                <div className="absolute top-full left-0 pt-0 w-64 z-50">
                  {/* Invisible bridge to prevent gap issues */}
                  <div className="h-1"></div>
                  <div className="bg-white rounded-xl shadow-xl border border-stone-100 py-2">
                    <Link 
                      to="/services" 
                      className="block px-4 py-2 hover:bg-emerald-50 hover:text-emerald-600 transition-colors font-medium" 
                      data-testid="nav-all-services"
                    >
                      All Services
                    </Link>
                    <div className="border-t border-stone-100 my-2"></div>
                    {servicesMenu.map((service) => (
                      <div 
                        key={service.name} 
                        className="relative"
                        onMouseEnter={() => handleSubmenuEnter(service.name)}
                        onMouseLeave={handleSubmenuLeave}
                      >
                        <Link 
                          to={service.path} 
                          className="flex items-center justify-between px-4 py-2 hover:bg-emerald-50 hover:text-emerald-600 transition-colors" 
                          data-testid={`nav-${service.name.toLowerCase()}`}
                        >
                          <span className="flex items-center gap-2">
                            <service.icon size={16} />
                            {service.name}
                          </span>
                          {service.submenu.length > 0 && <ChevronDown size={14} className="-rotate-90" />}
                        </Link>
                        
                        {service.submenu.length > 0 && activeSubmenu === service.name && (
                          <div 
                            className="absolute left-full top-0 pl-1 w-56"
                            onMouseEnter={() => handleSubmenuEnter(service.name)}
                            onMouseLeave={handleSubmenuLeave}
                          >
                            <div className="bg-white rounded-xl shadow-xl border border-stone-100 py-2">
                              {service.submenu.map((sub) => (
                                <Link 
                                  key={sub.path} 
                                  to={sub.path} 
                                  className="block px-4 py-2 hover:bg-emerald-50 hover:text-emerald-600 transition-colors" 
                                  data-testid={`nav-${sub.name.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <Link to="/specials" className={`nav-link ${isActive('/specials') ? 'text-emerald-600' : ''}`} data-testid="nav-specials">Specials</Link>
            <Link to="/comfort-loyalty" className={`nav-link ${isActive('/comfort-loyalty') ? 'text-emerald-600' : ''}`} data-testid="nav-loyalty">Comfort Loyalty</Link>
            <Link to="/blog" className={`nav-link ${isActive('/blog') ? 'text-emerald-600' : ''}`} data-testid="nav-blog">Blog</Link>
            <Link to="/faq" className={`nav-link ${isActive('/faq') ? 'text-emerald-600' : ''}`} data-testid="nav-faq">FAQ</Link>
            <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'text-emerald-600' : ''}`} data-testid="nav-contact">Contact Us</Link>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:8593576803" className="btn-primary flex items-center gap-2" data-testid="header-call-btn">
              <Phone size={18} />
              Call Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-stone-100">
            <div className="flex flex-col gap-2">
              <Link to="/" className="px-4 py-3 hover:bg-emerald-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-home">Home</Link>
              <Link to="/services" className="px-4 py-3 hover:bg-emerald-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-services">Services</Link>
              {servicesMenu.map((service) => (
                <React.Fragment key={service.name}>
                  <Link to={service.path} className="px-8 py-2 hover:bg-emerald-50 rounded-lg text-slate-600 flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                    <service.icon size={16} />
                    {service.name}
                  </Link>
                </React.Fragment>
              ))}
              <Link to="/specials" className="px-4 py-3 hover:bg-emerald-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-specials">Specials</Link>
              <Link to="/comfort-loyalty" className="px-4 py-3 hover:bg-emerald-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-loyalty">Comfort Loyalty</Link>
              <Link to="/blog" className="px-4 py-3 hover:bg-emerald-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-blog">Blog</Link>
              <Link to="/faq" className="px-4 py-3 hover:bg-emerald-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-faq">FAQ</Link>
              <Link to="/contact" className="px-4 py-3 hover:bg-emerald-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-contact">Contact Us</Link>
              <a href="tel:8593576803" className="mx-4 mt-4 btn-primary text-center" data-testid="mobile-call-btn">
                <Phone size={18} className="inline mr-2" />
                Call (859) 357-6803
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
